// Team1.js - Contains functions for creating a team section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Team section with team members
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the team section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The team section elements
 */
export const createTeamElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const teamSectionClass = 'team1-section';
  const teamContainerClass = 'team1-container';
  const teamHeaderClass = 'team1-header';
  const teamTitleClass = 'team1-title';
  const teamSubtitleClass = 'team1-subtitle';
  const teamCardsClass = 'team1-cards';
  const teamCardClass = 'team1-card';
  const teamMemberImageClass = 'team1-member-image';
  const teamMemberNameClass = 'team1-member-name';
  const teamMemberRoleClass = 'team1-member-role';
  const teamMemberBioClass = 'team1-member-bio';
  const teamSocialLinksClass = 'team1-social-links';
  const teamSocialLinkClass = 'team1-social-link';
  
  // Desktop Styles (default)
  const teamSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#ffffff',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const teamContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    maxWidth: '1200px',
    width: '100%',
    marginLeft: 'auto',
    marginRight: 'auto',
    gap: '60px'
  };

  const teamHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '20px',
    width: '100%',
    maxWidth: '700px'
  };

  const teamTitleStyles = {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '16px'
  };

  const teamSubtitleStyles = {
    fontSize: '18px',
    color: '#666666',
    lineHeight: '1.6',
    marginBottom: '30px'
  };

  const teamCardsStyles = {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '30px',
    width: '100%'
  };

  const teamCardStyles = {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#ffffff',
    borderRadius: '10px',
    boxShadow: '0 5px 20px rgba(0,0,0,0.1)',
    padding: '30px',
    flex: '1',
    minWidth: '280px',
    maxWidth: '320px',
    transition: 'transform 0.3s ease'
  };

  const teamMemberImageStyles = {
    width: '100%',
    height: 'auto',
    borderRadius: '10px',
    marginBottom: '20px',
    objectFit: 'cover',
    aspectRatio: '1/1'
  };

  const teamMemberNameStyles = {
    fontSize: '24px',
    fontWeight: 'bold',
    color: '#333333',
    marginBottom: '8px'
  };

  const teamMemberRoleStyles = {
    fontSize: '16px',
    color: '#4361ee',
    marginBottom: '16px',
    fontWeight: '500'
  };

  const teamMemberBioStyles = {
    fontSize: '16px',
    color: '#666666',
    marginBottom: '20px',
    lineHeight: '1.6'
  };

  const teamSocialLinksStyles = {
    display: 'flex',
    gap: '15px',
    marginTop: 'auto'
  };

  const teamSocialLinkStyles = {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f5f5f5',
    transition: 'background-color 0.3s ease'
  };

  // Tablet Styles
  const tabletTeamCardsStyles = {
    justifyContent: 'center',
    gap: '20px'
  };

  // Mobile Styles
  const mobileTeamCardsStyles = {
    flexDirection: 'column',
    alignItems: 'center'
  };

  const mobileTeamCardStyles = {
    width: '100%',
    maxWidth: '320px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(teamSectionClass, teamSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(teamContainerClass, teamContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(teamHeaderClass, teamHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(teamTitleClass, teamTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(teamSubtitleClass, teamSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(teamCardsClass, teamCardsStyles);
    newRegistry[DEVICES.DESKTOP].set(teamCardClass, teamCardStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberImageClass, teamMemberImageStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberNameClass, teamMemberNameStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberRoleClass, teamMemberRoleStyles);
    newRegistry[DEVICES.DESKTOP].set(teamMemberBioClass, teamMemberBioStyles);
    newRegistry[DEVICES.DESKTOP].set(teamSocialLinksClass, teamSocialLinksStyles);
    newRegistry[DEVICES.DESKTOP].set(teamSocialLinkClass, teamSocialLinkStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(teamCardsClass, tabletTeamCardsStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(teamCardsClass, mobileTeamCardsStyles);
    newRegistry[DEVICES.MOBILE].set(teamCardClass, mobileTeamCardStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    teamSectionClass,
    teamContainerClass,
    teamHeaderClass,
    teamTitleClass,
    teamSubtitleClass,
    teamCardsClass,
    teamCardClass,
    teamMemberImageClass,
    teamMemberNameClass,
    teamMemberRoleClass,
    teamMemberBioClass,
    teamSocialLinksClass,
    teamSocialLinkClass
  ]));
  
  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  
  // Header elements
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  
  // Team cards container
  const cardsContainerId = Date.now() + 5;
  
  // Team members
  const member1CardId = Date.now() + 6;
  const member1ImageId = Date.now() + 7;
  const member1NameId = Date.now() + 8;
  const member1RoleId = Date.now() + 9;
  const member1BioId = Date.now() + 10;
  const member1SocialLinksId = Date.now() + 11;
  const member1TwitterId = Date.now() + 12;
  const member1LinkedinId = Date.now() + 13;
  const member1EmailId = Date.now() + 14;
  
  const member2CardId = Date.now() + 15;
  const member2ImageId = Date.now() + 16;
  const member2NameId = Date.now() + 17;
  const member2RoleId = Date.now() + 18;
  const member2BioId = Date.now() + 19;
  const member2SocialLinksId = Date.now() + 20;
  const member2TwitterId = Date.now() + 21;
  const member2LinkedinId = Date.now() + 22;
  const member2EmailId = Date.now() + 23;
  
  const member3CardId = Date.now() + 24;
  const member3ImageId = Date.now() + 25;
  const member3NameId = Date.now() + 26;
  const member3RoleId = Date.now() + 27;
  const member3BioId = Date.now() + 28;
  const member3SocialLinksId = Date.now() + 29;
  const member3TwitterId = Date.now() + 30;
  const member3LinkedinId = Date.now() + 31;
  const member3EmailId = Date.now() + 32;
  
  const member4CardId = Date.now() + 33;
  const member4ImageId = Date.now() + 34;
  const member4NameId = Date.now() + 35;
  const member4RoleId = Date.now() + 36;
  const member4BioId = Date.now() + 37;
  const member4SocialLinksId = Date.now() + 38;
  const member4TwitterId = Date.now() + 39;
  const member4LinkedinId = Date.now() + 40;
  const member4EmailId = Date.now() + 41;

  const newElements = [
    // Main Team Section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Team Section',
      parentId: null,
      className: teamSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'team-section',
        'data-component': 'section',
        'data-section-type': 'team',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: teamContainerClass,
      styles: {},
      children: [headerId, cardsContainerId],
      text: '',
      attributes: {
        'data-element': 'team-container'
      }
    },
    
    // Header Section
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: teamHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'team-header'
      }
    },
    
    // Title 
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Team Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: teamTitleClass,
      styles: {},
      children: [],
      text: 'Meet Our Team',
      attributes: {
        'data-element': 'team-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: teamSubtitleClass,
      styles: {},
      children: [],
      text: 'Our talented team of professionals is dedicated to bringing you the best experience.',
      attributes: {
        'data-element': 'team-subtitle'
      }
    },
    
    // Team Cards Container
    {
      id: cardsContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Cards',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: teamCardsClass,
      styles: {},
      children: [member1CardId, member2CardId, member3CardId, member4CardId],
      text: '',
      attributes: {
        'data-element': 'team-cards'
      }
    },
    
    // Member 1 Card
    {
      id: member1CardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Member 1 Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: teamCardClass,
      styles: {},
      children: [member1ImageId, member1NameId, member1RoleId, member1BioId, member1SocialLinksId],
      text: '',
      attributes: {
        'data-element': 'team-card'
      }
    },
    
    // Member 1 Image
    {
      id: member1ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Team Member 1 Image',
      parentId: member1CardId,
      hierarchyName: getHierarchyName('image'),
      className: teamMemberImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80',
      attributes: {
        'data-element': 'team-member-image',
        'alt': 'Sarah Johnson'
      }
    },
    
    // Member 1 Name
    {
      id: member1NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Team Member 1 Name',
      parentId: member1CardId,
      hierarchyName: getHierarchyName('h3'),
      className: teamMemberNameClass,
      styles: {},
      children: [],
      text: 'Sarah Johnson',
      attributes: {
        'data-element': 'team-member-name'
      }
    },
    
    // Member 1 Role
    {
      id: member1RoleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Member 1 Role',
      parentId: member1CardId,
      hierarchyName: getHierarchyName('p'),
      className: teamMemberRoleClass,
      styles: {},
      children: [],
      text: 'CEO & Founder',
      attributes: {
        'data-element': 'team-member-role'
      }
    },
    
    // Member 1 Bio
    {
      id: member1BioId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Member 1 Bio',
      parentId: member1CardId,
      hierarchyName: getHierarchyName('p'),
      className: teamMemberBioClass,
      styles: {},
      children: [],
      text: 'Sarah leads our company with over 15 years of experience in the industry. Her vision drives our innovation.',
      attributes: {
        'data-element': 'team-member-bio'
      }
    },
    
    // Member 1 Social Links
    {
      id: member1SocialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Member 1 Social Links',
      parentId: member1CardId,
      hierarchyName: getHierarchyName('div'),
      className: teamSocialLinksClass,
      styles: {},
      children: [member1TwitterId, member1LinkedinId, member1EmailId],
      text: '',
      attributes: {
        'data-element': 'team-social-links'
      }
    },
    
    // Member 1 Twitter
    {
      id: member1TwitterId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Twitter Link',
      parentId: member1SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 100],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Twitter'
      }
    },
    // Twitter Icon
    {
      id: Date.now() + 100,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: member1TwitterId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Twitter'
      }
    },
    
    // Member 1 LinkedIn
    {
      id: member1LinkedinId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'LinkedIn Link',
      parentId: member1SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 101],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'LinkedIn'
      }
    },
    // LinkedIn Icon
    {
      id: Date.now() + 101,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: member1LinkedinId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:linkedin.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'LinkedIn'
      }
    },
    
    // Member 1 Email
    {
      id: member1EmailId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Email Link',
      parentId: member1SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 102],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Email'
      }
    },
    // Email Icon
    {
      id: Date.now() + 102,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Email Icon',
      parentId: member1EmailId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:email.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Email'
      }
    },
    
    // Member 2 Card
    {
      id: member2CardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Member 2 Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: teamCardClass,
      styles: {},
      children: [member2ImageId, member2NameId, member2RoleId, member2BioId, member2SocialLinksId],
      text: '',
      attributes: {
        'data-element': 'team-card'
      }
    },
    
    // Member 2 Image
    {
      id: member2ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Team Member 2 Image',
      parentId: member2CardId,
      hierarchyName: getHierarchyName('image'),
      className: teamMemberImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1600486913747-55e5470d6f40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80',
      attributes: {
        'data-element': 'team-member-image',
        'alt': 'Michael Chen'
      }
    },
    
    // Member 2 Name
    {
      id: member2NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Team Member 2 Name',
      parentId: member2CardId,
      hierarchyName: getHierarchyName('h3'),
      className: teamMemberNameClass,
      styles: {},
      children: [],
      text: 'Michael Chen',
      attributes: {
        'data-element': 'team-member-name'
      }
    },
    
    // Member 2 Role
    {
      id: member2RoleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Member 2 Role',
      parentId: member2CardId,
      hierarchyName: getHierarchyName('p'),
      className: teamMemberRoleClass,
      styles: {},
      children: [],
      text: 'CTO',
      attributes: {
        'data-element': 'team-member-role'
      }
    },
    
    // Member 2 Bio
    {
      id: member2BioId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Member 2 Bio',
      parentId: member2CardId,
      hierarchyName: getHierarchyName('p'),
      className: teamMemberBioClass,
      styles: {},
      children: [],
      text: 'Michael oversees all technical aspects of our platform with his expertise in software architecture and development.',
      attributes: {
        'data-element': 'team-member-bio'
      }
    },
    
    // Member 2 Social Links
    {
      id: member2SocialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Member 2 Social Links',
      parentId: member2CardId,
      hierarchyName: getHierarchyName('div'),
      className: teamSocialLinksClass,
      styles: {},
      children: [member2TwitterId, member2LinkedinId, member2EmailId],
      text: '',
      attributes: {
        'data-element': 'team-social-links'
      }
    },
    
    // Member 2 Twitter
    {
      id: member2TwitterId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Twitter Link',
      parentId: member2SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 103],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Twitter'
      }
    },
    // Twitter Icon
    {
      id: Date.now() + 103,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: member2TwitterId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Twitter'
      }
    },
    
    // Member 2 LinkedIn
    {
      id: member2LinkedinId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'LinkedIn Link',
      parentId: member2SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 104],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'LinkedIn'
      }
    },
    // LinkedIn Icon
    {
      id: Date.now() + 104,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: member2LinkedinId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:linkedin.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'LinkedIn'
      }
    },
    
    // Member 2 Email
    {
      id: member2EmailId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Email Link',
      parentId: member2SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 105],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Email'
      }
    },
    // Email Icon
    {
      id: Date.now() + 105,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Email Icon',
      parentId: member2EmailId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:email.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Email'
      }
    },
    
    // Member 3 Card
    {
      id: member3CardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Member 3 Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: teamCardClass,
      styles: {},
      children: [member3ImageId, member3NameId, member3RoleId, member3BioId, member3SocialLinksId],
      text: '',
      attributes: {
        'data-element': 'team-card'
      }
    },
    
    // Member 3 Image
    {
      id: member3ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Team Member 3 Image',
      parentId: member3CardId,
      hierarchyName: getHierarchyName('image'),
      className: teamMemberImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80',
      attributes: {
        'data-element': 'team-member-image',
        'alt': 'Emma Rodriguez'
      }
    },
    
    // Member 3 Name
    {
      id: member3NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Team Member 3 Name',
      parentId: member3CardId,
      hierarchyName: getHierarchyName('h3'),
      className: teamMemberNameClass,
      styles: {},
      children: [],
      text: 'Emma Rodriguez',
      attributes: {
        'data-element': 'team-member-name'
      }
    },
    
    // Member 3 Role
    {
      id: member3RoleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Member 3 Role',
      parentId: member3CardId,
      hierarchyName: getHierarchyName('p'),
      className: teamMemberRoleClass,
      styles: {},
      children: [],
      text: 'Lead Designer',
      attributes: {
        'data-element': 'team-member-role'
      }
    },
    
    // Member 3 Bio
    {
      id: member3BioId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Member 3 Bio',
      parentId: member3CardId,
      hierarchyName: getHierarchyName('p'),
      className: teamMemberBioClass,
      styles: {},
      children: [],
      text: 'Emma brings creativity and user-focused design principles to every project, creating beautiful and intuitive interfaces.',
      attributes: {
        'data-element': 'team-member-bio'
      }
    },
    
    // Member 3 Social Links
    {
      id: member3SocialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Member 3 Social Links',
      parentId: member3CardId,
      hierarchyName: getHierarchyName('div'),
      className: teamSocialLinksClass,
      styles: {},
      children: [member3TwitterId, member3LinkedinId, member3EmailId],
      text: '',
      attributes: {
        'data-element': 'team-social-links'
      }
    },
    
    // Member 3 Twitter
    {
      id: member3TwitterId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Twitter Link',
      parentId: member3SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 106],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Twitter'
      }
    },
    // Twitter Icon
    {
      id: Date.now() + 106,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: member3TwitterId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Twitter'
      }
    },
    
    // Member 3 LinkedIn
    {
      id: member3LinkedinId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'LinkedIn Link',
      parentId: member3SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 107],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'LinkedIn'
      }
    },
    // LinkedIn Icon
    {
      id: Date.now() + 107,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: member3LinkedinId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:linkedin.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'LinkedIn'
      }
    },
    
    // Member 3 Email
    {
      id: member3EmailId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Email Link',
      parentId: member3SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 108],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Email'
      }
    },
    // Email Icon
    {
      id: Date.now() + 108,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Email Icon',
      parentId: member3EmailId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:email.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Email'
      }
    },
    
    // Member 4 Card
    {
      id: member4CardId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Member 4 Card',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: teamCardClass,
      styles: {},
      children: [member4ImageId, member4NameId, member4RoleId, member4BioId, member4SocialLinksId],
      text: '',
      attributes: {
        'data-element': 'team-card'
      }
    },
    
    // Member 4 Image
    {
      id: member4ImageId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Team Member 4 Image',
      parentId: member4CardId,
      hierarchyName: getHierarchyName('image'),
      className: teamMemberImageClass,
      styles: {},
      src: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=600&q=80',
      attributes: {
        'data-element': 'team-member-image',
        'alt': 'David Kim'
      }
    },
    
    // Member 4 Name
    {
      id: member4NameId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Team Member 4 Name',
      parentId: member4CardId,
      hierarchyName: getHierarchyName('h3'),
      className: teamMemberNameClass,
      styles: {},
      children: [],
      text: 'David Kim',
      attributes: {
        'data-element': 'team-member-name'
      }
    },
    
    // Member 4 Role
    {
      id: member4RoleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Member 4 Role',
      parentId: member4CardId,
      hierarchyName: getHierarchyName('p'),
      className: teamMemberRoleClass,
      styles: {},
      children: [],
      text: 'Marketing Director',
      attributes: {
        'data-element': 'team-member-role'
      }
    },
    
    // Member 4 Bio
    {
      id: member4BioId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Team Member 4 Bio',
      parentId: member4CardId,
      hierarchyName: getHierarchyName('p'),
      className: teamMemberBioClass,
      styles: {},
      children: [],
      text: 'David leads our marketing efforts with innovative strategies and deep industry insights to grow our customer base.',
      attributes: {
        'data-element': 'team-member-bio'
      }
    },
    
    // Member 4 Social Links
    {
      id: member4SocialLinksId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Team Member 4 Social Links',
      parentId: member4CardId,
      hierarchyName: getHierarchyName('div'),
      className: teamSocialLinksClass,
      styles: {},
      children: [member4TwitterId, member4LinkedinId, member4EmailId],
      text: '',
      attributes: {
        'data-element': 'team-social-links'
      }
    },
    
    // Member 4 Twitter
    {
      id: member4TwitterId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Twitter Link',
      parentId: member4SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 109],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Twitter'
      }
    },
    // Twitter Icon
    {
      id: Date.now() + 109,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Twitter Icon',
      parentId: member4TwitterId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:twitter.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Twitter'
      }
    },
    
    // Member 4 LinkedIn
    {
      id: member4LinkedinId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'LinkedIn Link',
      parentId: member4SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 110],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'LinkedIn'
      }
    },
    // LinkedIn Icon
    {
      id: Date.now() + 110,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'LinkedIn Icon',
      parentId: member4LinkedinId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:linkedin.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'LinkedIn'
      }
    },
    
    // Member 4 Email
    {
      id: member4EmailId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Email Link',
      parentId: member4SocialLinksId,
      hierarchyName: getHierarchyName('link'),
      className: teamSocialLinkClass,
      styles: {},
      children: [Date.now() + 111],
      text: '',
      attributes: {
        'data-element': 'social-link',
        'href': '#',
        'target': '_blank',
        'rel': 'noopener noreferrer',
        'aria-label': 'Email'
      }
    },
    // Email Icon
    {
      id: Date.now() + 111,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Email Icon',
      parentId: member4EmailId,
      hierarchyName: getHierarchyName('image'),
      styles: {
        width: '16px',
        height: '16px'
      },
      src: 'https://api.iconify.design/mdi:email.svg?color=%234361ee',
      attributes: {
        'data-element': 'social-icon',
        'alt': 'Email'
      }
    }
  ];
  
  return {
    teamSection: newElements[0],
    elements: newElements
  };
};

export default createTeamElements; 